import {
  faCancel,
  faEdit,
  faExternalLink,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import KeyValueList from 'components/KeyValueList';
import Modal from 'components/Modal';
import useTranslations from 'contexts/basicData/useTranslations';
import useModalStack from 'contexts/modal/useModalStack';
import { FC, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ApiResponse, useApiCallFn } from 'hooks/useApiCallFn';
import { formatDateTime } from 'utils/date-helpers';
import { arraySpreadIf } from 'utils/spreading';
import { useDetailedCaseContext } from '../DetailedCaseProvider';
import {
  CaseEventType,
  CaseExtendedModel,
  CaseModel,
  CaseTypeEnum,
} from 'api/model';
import AuthorizedTextButton from 'components/inputs/AuthorizedTextButton';
import Roles from 'constants/Roles';
import useAssignmentWarnings from 'hooks/useAssignmentWarnings';
import { formatAddress } from 'utils/address';
import OriginalAddressEditModal from './OriginalAddressEditModal';
import AutobasLoginInfo from './AutobasLoginInfo';
import {
  CaseDataField,
  caseDataFormToCaseModel,
  effectiveSendCancellationSmsToBooker,
  initializeCaseDataForm,
} from '../DetailedCaseFormProvider/useCaseDataForm';
import useZipCodeAreas from 'hooks/useZipCodeAreas';
import { useCaseUpdateCase, useCaseUpdateCaseHook } from 'api/case/case';
import toast from 'react-hot-toast';
import ActionModal from 'components/Modal/ActionModal';
import { requiresExplicitDirectBookingFlag } from 'utils/sameDayDirektbok';
import {
  isStaffRentalCaseType,
  isVehicleLessCaseType,
} from '../CaseAssignments/utils';

const Wrapper = styled.div``;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
`;

const StyledLink = styled.a`
  text-decoration: none;
`;

const RightTextButton = styled(AuthorizedTextButton)`
  margin-left: auto;
  text-align: right;
`;

const BigLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  font-size: 50px;
  font-weight: bold;
  ${({ theme }) => css`
    color: ${theme.colors.foreground.tertiary};
  `}
  // only upper-case
  text-transform: uppercase;
`;

const Warning = styled.div`
  ${({ theme }) => css`
    color: ${theme.colors.background.negative};
  `}
`;

type ChipTone = 'category' | 'premium' | 'normal' | 'warn' | 'risk' | 'neutral';

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const DirektbokToggleGroup = styled.div`
  display: flex;
  width: 100%;
  margin-top: 12px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #d1d5db;
`;

const DirektbokToggleButton = styled.button<{
  $selected: boolean;
  $variant: 'yes' | 'no';
}>`
  flex: 1;
  padding: 8px 16px;
  border: none;
  cursor: pointer;
  font-size: 0.95em;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  transition: all 0.15s ease;

  & + & {
    border-left: 1px solid #d1d5db;
  }

  ${({ $selected, $variant, theme }) => {
    if (!$selected) {
      return css`
        background: #fff;
        color: ${theme.colors.foreground.primary};
        &:hover {
          background: ${theme.colors.background.secondary};
        }
      `;
    }
    if ($variant === 'yes') {
      return css`
        background: ${theme.colors.background.positive};
        color: #fff;
      `;
    }
    return css`
      background: ${theme.colors.background.negative};
      color: #fff;
    `;
  }}
`;

const Chip = styled.span<{ $tone: ChipTone; $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 0.85em;
  font-weight: 500;
  border-radius: 999px;
  white-space: nowrap;
  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover {
        filter: brightness(0.95);
      }
    `}
  ${({ $tone }) => {
    switch ($tone) {
      case 'category':
        return css`
          background: #e3edff;
          color: #1d4ed8;
        `;
      case 'premium':
        return css`
          background: #fef3c7;
          color: #92400e;
        `;
      case 'warn':
        return css`
          background: #ffedd5;
          color: #9a3412;
        `;
      case 'risk':
        return css`
          background: #fee2e2;
          color: #991b1b;
        `;
      case 'normal':
        return css`
          background: #ecfdf5;
          color: #065f46;
        `;
      case 'neutral':
      default:
        return css`
          background: #f1f5f9;
          color: #475569;
        `;
    }
  }}
`;

const AddressRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const AddressContent = styled.div`
  flex: 1;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.foreground.tint};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.foreground.primary};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.border.primary};
    outline-offset: 2px;
  }
`;

interface UnmarkAsInvoicedModalProps {
  caseResponse: ApiResponse<CaseExtendedModel | undefined>;
}
const UnmarkAsInvoicedModal: FC<UnmarkAsInvoicedModalProps> = ({
  caseResponse,
}) => {
  const modalStack = useModalStack();

  const updateCase = useCaseUpdateCaseHook();
  const updateAssignmentCall = useApiCallFn((caseModel: CaseModel) =>
    updateCase(caseModel, {
      // CaseEventType (named-value union) and CaseUpdateCaseEventType (NUMBER_xx
      // union) share numeric values but are distinct TS types in Orval.
      eventType: CaseEventType.UnmarkedAsInvoiced,
      eventMessage: 'Avmarkerade ärendet som fakturerat',
    })
  );

  return (
    <Modal
      buttons={[
        {
          label: 'Kör på!',
          onClick: async () => {
            if (caseResponse.response?.case) {
              const updatedCase: CaseModel = {
                ...caseResponse.response.case,
                invoiceDataID: null as unknown as number | undefined,
              };

              const [, error] = await updateAssignmentCall.run(updatedCase);
              if (!error) {
                modalStack.pop();
                caseResponse.update();
              }
            }
          },
        },
        {
          icon: <FontAwesomeIcon icon={faCancel} />,
          label: 'Avbryt',
          onClick: () => modalStack.pop(),
        },
      ]}
      title="Avmarkera som fakturerat"
    >
      Ärendet kommer att kunna ingå i nästa fakturaunderlag, men det kommer
      fortfarande finnas kvar i den redan genererade Excel-exporten från sin
      tidigare fakturering. Är du säker på att du vill fortsätta?
    </Modal>
  );
};

interface Props {
  caseResponse: CaseExtendedModel;
  className?: string;
}

const CaseDataViewer: FC<Props> = ({ caseResponse, className }) => {
  const assignmentWarnings = useAssignmentWarnings();
  const { caseTypes, caseStatuses, tyreTypes } = useTranslations();
  const { caseResponse: response } = useDetailedCaseContext();
  const modalStack = useModalStack();
  const { allZipCodeAreas: zipCodeAreas } = useZipCodeAreas();

  const handleRemoveInvoiceDataIdClick = () => {
    modalStack.push(<UnmarkAsInvoicedModal caseResponse={response} />);
  };

  // const updateCaseCall = useApiCall(CaseClient, (c, caseModel: CaseModel) =>
  //   c.updateCase(
  //     caseModel,
  //     CaseEventType.CaseEdited,
  //     'Uppdaterade originaladresser'
  //   )
  // );

  const updateCaseCall = useCaseUpdateCase();

  const handleEditAddressClick = () => {
    modalStack.push(
      <OriginalAddressEditModal
        caseData={caseResponse}
        onSave={async (addressData) => {
          // Convert the address data to the form format
          const currentForm = initializeCaseDataForm(caseResponse.case);
          const updatedForm = {
            ...currentForm,
            [CaseDataField.originalFromName]: addressData.originalFromName,
            [CaseDataField.originalFromAddress]:
              addressData.originalFromAddress,
            [CaseDataField.originalFromCity]: addressData.originalFromCity,
            [CaseDataField.originalFromZip]: addressData.originalFromZip,
            [CaseDataField.originalFromCounty]: addressData.originalFromCounty,
            [CaseDataField.originalToName]: addressData.originalToName,
            [CaseDataField.originalToAddress]: addressData.originalToAddress,
            [CaseDataField.originalToCity]: addressData.originalToCity,
            [CaseDataField.originalToZip]: addressData.originalToZip,
            [CaseDataField.originalToCounty]: addressData.originalToCounty,
          };

          // Convert to CaseModel and update
          const zipCodeAreasById = Object.fromEntries(
            zipCodeAreas.map((area) => [area.id.toString(), area])
          );

          // Debug logging - remove in production
          const updatedCase = caseDataFormToCaseModel(
            caseResponse.case,
            updatedForm,
            zipCodeAreasById
          );

          try {
            await updateCaseCall.mutateAsync({
              data: { ...updatedCase },
            });
            response.update();
          } catch (error) {
            toast.error('Det gick inte att uppdatera ärendet. Försök igen.');
            // console.error('Failed to update case:', error);
          }
        }}
      />
    );
  };

  const {
    case: {
      caseTypeID,
      caseStatusID,
      closedDate,
      completedDate,
      registrationNumber,
      vehicleGearBox,
      vehicleMileage,
      vehicleYearModel,
      customerName,
      customerPhoneNumber,
      bookerName,
      bookerPhoneNumber,
      isImportant,
      // timestamp,
      company,
      externalTestUrl,
      externalProtocolUrl,
      // assignmentList,
      // hasLinkedCases,
      // rideOrderID,
      // createdByID,
      // createdBy,
      // modifiedByID,
      // modifiedBy,
      isElectric,
      isPremiumCar,
      isTrailer,
      isTruck,
      isSupercar,
      isTransportVehicle,
      isHighMileage,
      isOlderModel,
      isRiskVehicle,
      isLowRangeElectric,
      isDirectBooking,
      sendCustomerSurvey,
      sendCancellationSmsToBooker,
      vehicleBrand,
      vehicleModel,
      vehicleBody,
      vehicleFuel,
      vehicleHorsePower,
      vehicleValuation,
      vehicleType,
      carTransportTruck,
      wltpEnergyConsumptionKwhPer100Km,
      wltpFuelConsumptionLitersPer100Km,
      energyCapacityKwh,
      serviceIntervalKm,
      serviceIntervalMonths,
      timingBeltIntervalKm,
      timingBeltIntervalMonths,
    },
  } = caseResponse;

  const saveDirektbok = useCallback(
    async (newValue: boolean | null) => {
      const updatedCase: CaseModel = {
        ...caseResponse.case,
        isDirectBooking: newValue ?? undefined,
      } as CaseModel;
      try {
        await updateCaseCall.mutateAsync({
          data: updatedCase,
          params: {
            eventType: CaseEventType.CaseEdited,
            eventMessage:
              newValue === true
                ? 'Markerade ärendet som direktbok'
                : newValue === false
                  ? 'Markerade ärendet som inte direktbok'
                  : 'Tog bort direktbok-svar',
          },
        });
        response.update();
      } catch {
        toast.error(
          'Det gick inte att uppdatera direktbok-svaret. Försök igen.'
        );
      }
    },
    [caseResponse.case, updateCaseCall, response]
  );

  const handleEditDirektbokClick = useCallback(() => {
    const pick = (next: boolean) => {
      modalStack.pop();
      // Inget skickas om värdet är oförändrat — toggle:n låter användaren
      // bekräfta nuvarande status utan onödigt API-anrop.
      if (next !== isDirectBooking) {
        saveDirektbok(next);
      }
    };
    modalStack.push(
      <ActionModal
        title="Ändra direktbok-status"
        onClose={() => modalStack.pop()}
        actions={[
          {
            label: 'Avbryt',
            onClick: () => modalStack.pop(),
            color: 'secondary',
          },
        ]}
      >
        <span>
          Direktbok (skapat samma dag som utförandet) faktureras som direktbok i
          vissa fall.
        </span>
        <DirektbokToggleGroup role="group" aria-label="Direktbok-svar">
          <DirektbokToggleButton
            type="button"
            $variant="no"
            $selected={isDirectBooking === false}
            aria-pressed={isDirectBooking === false}
            onClick={() => pick(false)}
          >
            Nej
          </DirektbokToggleButton>
          <DirektbokToggleButton
            type="button"
            $variant="yes"
            $selected={isDirectBooking === true}
            aria-pressed={isDirectBooking === true}
            onClick={() => pick(true)}
          >
            Ja, fakturera direktbok
          </DirektbokToggleButton>
        </DirektbokToggleGroup>
      </ActionModal>
    );
  }, [modalStack, saveDirektbok, isDirectBooking]);

  const getFlags = useCallback(() => {
    const chips: { label: string; tone: ChipTone }[] = [];
    if (isImportant) chips.push({ label: 'Viktigt', tone: 'risk' });
    // "Ska på lastbil" ligger per uppdrag (ben) och visas i uppdragskortet, inte som ärendechip.
    if (isElectric) chips.push({ label: 'Elbil', tone: 'category' });
    if (isLowRangeElectric)
      chips.push({
        label: 'Dålig räckvidd – rekommenderas lastbil',
        tone: 'warn',
      });
    if (isSupercar) chips.push({ label: 'Superbil', tone: 'category' });
    if (isTransportVehicle)
      chips.push({ label: 'Transportbil', tone: 'category' });
    if (isPremiumCar) chips.push({ label: 'Premium', tone: 'premium' });
    if (isHighMileage) chips.push({ label: 'Högmilad', tone: 'warn' });
    if (isOlderModel) chips.push({ label: 'Äldre modell', tone: 'warn' });
    if (isRiskVehicle) chips.push({ label: 'Riskbil', tone: 'risk' });
    if (isTrailer) chips.push({ label: 'Släp', tone: 'neutral' });
    if (isTruck) chips.push({ label: 'Lastbil', tone: 'neutral' });

    if (chips.length === 0) return '-';

    return (
      <ChipRow>
        {chips.map((c) => (
          <Chip key={c.label} $tone={c.tone}>
            {c.label}
          </Chip>
        ))}
      </ChipRow>
    );
  }, [
    isImportant,
    isElectric,
    isSupercar,
    isTransportVehicle,
    isPremiumCar,
    isHighMileage,
    isOlderModel,
    isRiskVehicle,
    isLowRangeElectric,
    isTrailer,
    isTruck,
  ]);

  // Direktbok-svar visas på en egen rad (Sump/Annons) — alltid klickbar, även
  // när svaret är null, så admin kan svara/ändra utan att gå via spar-flödet.
  // Null visas som "Ej satt" (grå) — TL har inte aktivt valt än, snurran
  // behandlar det som icke-direktbok tills TL svarat. Explicit "Nej" får
  // röd ton för att signalera ett aktivt val.
  const getDirektbokValue = useCallback(() => {
    let label: string;
    let tone: ChipTone;
    if (isDirectBooking === true) {
      label = 'Ja, fakturera direktbok';
      tone = 'normal';
    } else if (isDirectBooking === false) {
      label = 'Nej';
      tone = 'risk';
    } else {
      label = 'Ej satt';
      tone = 'neutral';
    }
    return (
      <Chip
        $tone={tone}
        $clickable
        onClick={handleEditDirektbokClick}
        title="Klicka för att ändra"
      >
        {label}
      </Chip>
    );
  }, [isDirectBooking, handleEditDirektbokClick]);

  const showDirektbokRow = requiresExplicitDirectBookingFlag(caseResponse.case);

  const hideIfCarTransportTruck = useCallback(
    () => caseTypeID === CaseTypeEnum.CarTransportTruck,
    [caseTypeID]
  );

  // Uthyrd personal, Samåkning och Däckkörning: fordonsfälten döljs (Regnr/Miltal och hela
  // kolumnerna med fordonsdata, flaggor, original-adresser). För Uthyrd personal och Däckkörning
  // behålls Kund/Kund tel; för Samåkning döljs de (isSamakning nedan). Raden Flaggor (med
  // Viktigt) och original-adresserna döljs bara för Uthyrd personal-formulärets typer —
  // Däckkörning har dem som Avvikande jobb.
  const isStaffRental = isVehicleLessCaseType(caseTypeID);
  const isStaffRentalForm = isStaffRentalCaseType(caseTypeID);
  const isSamakning = caseTypeID === CaseTypeEnum.Samakning;

  const columnOne = useMemo(() => {
    const result = [
      { key: 'Typ', value: caseTypes[caseTypeID] },
      { key: 'Status', value: caseStatuses[caseStatusID] },
      ...arraySpreadIf(!!closedDate, {
        key: 'Stängdes',
        value: closedDate ? formatDateTime(closedDate) : '-',
      }),
      ...arraySpreadIf(!!completedDate, {
        key: 'Slutfört',
        value: completedDate ? formatDateTime(completedDate) : '-',
      }),
      { key: 'Företag', value: company?.name },
      {
        key: 'Lastbil',
        value: carTransportTruck?.registrationNumber,
        exclude: () => !hideIfCarTransportTruck(),
      },
      {
        key: 'Bokare',
        value: bookerName,
        exclude: hideIfCarTransportTruck,
      },
      {
        key: 'Bokare tel',
        value: bookerPhoneNumber,
        exclude: hideIfCarTransportTruck,
      },
    ];

    return result?.filter((r) => (r.exclude ? !r.exclude?.() : true));
  }, [
    bookerName,
    bookerPhoneNumber,
    carTransportTruck?.registrationNumber,
    caseStatusID,
    caseStatuses,
    caseTypeID,
    caseTypes,
    closedDate,
    completedDate,
    company?.name,
    hideIfCarTransportTruck,
  ]);

  const columnTwo = useMemo(() => {
    // Kund/Kund tel visas även för Uthyrd personal (företaget dit FT hyrs ut), men döljs
    // för Samåkning. Fordonsfälten (Regnr/Miltal) döljs för båda.
    const result = [
      {
        key: 'Kund',
        value: customerName,
        exclude: () => hideIfCarTransportTruck() || isSamakning,
      },
      {
        key: 'Kund tel',
        value: customerPhoneNumber,
        exclude: () => hideIfCarTransportTruck() || isSamakning,
      },
      {
        key: 'Regnr',
        value: (
          <>
            {registrationNumber} {vehicleGearBox ? ' M' : ' A'}
          </>
        ),
        exclude: () => hideIfCarTransportTruck() || isStaffRental,
      },
      {
        key: 'Miltal',
        value: vehicleMileage,
        exclude: () => hideIfCarTransportTruck() || isStaffRental,
      },
    ];

    return result?.filter((r) => (r.exclude ? !r.exclude?.() : true));
  }, [
    customerName,
    customerPhoneNumber,
    hideIfCarTransportTruck,
    isStaffRental,
    isSamakning,
    registrationNumber,
    vehicleGearBox,
    vehicleMileage,
  ]);

  const columnThree = useMemo(() => {
    if (isStaffRental) return [];
    const result = [
      {
        key: 'Testprotokoll',
        value:
          externalTestUrl !== '' ? (
            <StyledLink href={externalTestUrl} rel="noreferrer" target="_blank">
              Öppna extern länk <FontAwesomeIcon icon={faExternalLink} />
            </StyledLink>
          ) : (
            'Saknas'
          ),
        exclude: hideIfCarTransportTruck,
      },
      {
        key: 'Inspektionsprotokoll',
        value:
          externalProtocolUrl !== '' ? (
            <StyledLink
              href={externalProtocolUrl}
              rel="noreferrer"
              target="_blank"
            >
              Öppna extern länk <FontAwesomeIcon icon={faExternalLink} />
            </StyledLink>
          ) : (
            'Saknas'
          ),
        exclude: hideIfCarTransportTruck,
      },
      ...arraySpreadIf(
        (caseResponse.case.vehicleTyreType !== undefined &&
          caseResponse.case.caseTypeID === CaseTypeEnum.InternalDelivery) ||
          caseResponse.case.caseTypeID === CaseTypeEnum.RentedStaff,
        {
          key: 'Däcktyp',
          value: tyreTypes[caseResponse.case.vehicleTyreType!],
          exclude: hideIfCarTransportTruck,
        }
      ),
      {
        key: 'Skicka kundundersökning',
        value: sendCustomerSurvey ? 'Ja' : 'Nej',
        exclude: hideIfCarTransportTruck,
      },
      ...arraySpreadIf(!isVehicleLessCaseType(caseResponse.case.caseTypeID), {
        key: 'SMS till säljare vid avbokning',
        value: effectiveSendCancellationSmsToBooker(
          caseResponse.case.caseTypeID,
          sendCancellationSmsToBooker
        )
          ? 'Ja'
          : 'Nej',
        exclude: hideIfCarTransportTruck,
      }),
      ...arraySpreadIf(!!isElectric, {
        key: 'WLTP',
        value: `${wltpEnergyConsumptionKwhPer100Km} kWh/100km, ${energyCapacityKwh} kWh batterikapacitet`,
        exclude: hideIfCarTransportTruck,
      }),
      ...arraySpreadIf(!isElectric && !!wltpFuelConsumptionLitersPer100Km, {
        key: 'WLTP Bränsleförbrukning (l/100km)',
        value: wltpFuelConsumptionLitersPer100Km,
        exclude: hideIfCarTransportTruck,
      }),
      ...arraySpreadIf(!!serviceIntervalKm || !!serviceIntervalMonths, {
        key: 'Serviceintervall',
        value: `${serviceIntervalKm} km / ${serviceIntervalMonths} månader`,
        exclude: hideIfCarTransportTruck,
      }),
      {
        key: 'Kamremsintervall',
        value: (() => {
          // Ride-data är opålitlig för bilmodeller yngre än 1 år.
          const parsedYearModel = vehicleYearModel
            ? parseInt(vehicleYearModel, 10)
            : NaN;
          if (
            Number.isFinite(parsedYearModel) &&
            new Date().getFullYear() - parsedYearModel < 1
          ) {
            return <AutobasLoginInfo />;
          }
          return timingBeltIntervalKm || timingBeltIntervalMonths ? (
            `${timingBeltIntervalKm ? timingBeltIntervalKm / 10 : '-'} mil / ${timingBeltIntervalMonths ?? '-'} månader`
          ) : (
            <AutobasLoginInfo />
          );
        })(),
        exclude: hideIfCarTransportTruck,
      },
    ];

    return result?.filter((r) => (r.exclude ? !r.exclude?.() : true));
  }, [
    externalTestUrl,
    hideIfCarTransportTruck,
    isStaffRental,
    externalProtocolUrl,
    caseResponse.case.vehicleTyreType,
    caseResponse.case.caseTypeID,
    tyreTypes,
    sendCustomerSurvey,
    sendCancellationSmsToBooker,
    isElectric,
    wltpEnergyConsumptionKwhPer100Km,
    energyCapacityKwh,
    wltpFuelConsumptionLitersPer100Km,
    serviceIntervalKm,
    serviceIntervalMonths,
    timingBeltIntervalKm,
    timingBeltIntervalMonths,
    vehicleYearModel,
  ]);

  const columnFour = useMemo(() => {
    if (isStaffRental) return [];
    const result = [
      {
        key: 'Årsmodell',
        value: vehicleYearModel ?? '-',
        exclude: hideIfCarTransportTruck,
      },
      {
        key: 'Märke',
        value: vehicleBrand ?? '-',
        exclude: hideIfCarTransportTruck,
      },
      {
        key: 'Modell',
        value: vehicleModel ?? '-',
        exclude: hideIfCarTransportTruck,
      },
      {
        key: 'Karosstyp',
        value: vehicleBody ?? '-',
        exclude: hideIfCarTransportTruck,
      },
      {
        key: 'Drivmedel',
        value: vehicleFuel ?? '-',
        exclude: hideIfCarTransportTruck,
      },
      {
        key: 'Typ',
        value: vehicleType ?? '-',
        exclude: hideIfCarTransportTruck,
      },
      {
        key: 'Hästkrafter',
        value: vehicleHorsePower ?? '-',
        exclude: hideIfCarTransportTruck,
      },
      {
        key: 'Värdering',
        value: vehicleValuation?.toLocaleString('sv-SE') ?? '-',
        exclude: hideIfCarTransportTruck,
      },
    ];
    return result?.filter((r) => (r.exclude ? !r.exclude?.() : true));
  }, [
    hideIfCarTransportTruck,
    isStaffRental,
    vehicleYearModel,
    vehicleBrand,
    vehicleModel,
    vehicleBody,
    vehicleFuel,
    vehicleType,
    vehicleHorsePower,
    vehicleValuation,
  ]);

  return (
    <Wrapper className={className}>
      <Row>
        {caseTypeID === CaseTypeEnum.Placeholder ? (
          <BigLabel>
            <FontAwesomeIcon icon={faWarning} />
            {caseTypes[caseTypeID]}
            <FontAwesomeIcon icon={faWarning} />
          </BigLabel>
        ) : (
          <>
            <KeyValueList colonKey rows={columnOne} />
            <KeyValueList colonKey rows={columnTwo} />
            <KeyValueList colonKey rows={columnFour} />
            <KeyValueList colonKey rows={columnThree} />
            {Number.isInteger(caseResponse.case.invoiceDataID) && (
              <RightTextButton
                onClick={handleRemoveInvoiceDataIdClick}
                roles={[Roles.Ekonomi]}
              >
                Avmarkera som fakturerat
              </RightTextButton>
            )}
          </>
        )}
      </Row>
      {caseTypeID !== CaseTypeEnum.Placeholder &&
        caseTypeID !== CaseTypeEnum.CarTransportTruck &&
        !isStaffRentalForm && (
          <Row style={{ marginTop: 10 }}>
            <KeyValueList
              colonKey
              rows={[{ key: 'Flaggor', value: getFlags() }]}
            />
          </Row>
        )}
      {showDirektbokRow && (
        <Row style={{ marginTop: 10 }}>
          <KeyValueList
            colonKey
            rows={[{ key: 'Direktbok', value: getDirektbokValue() }]}
          />
        </Row>
      )}
      {
        // show only if there is an original address (aldrig för Uthyrd personal,
        // som inte har separata original-/till-adresser)
        !isStaffRentalForm &&
          (caseResponse.case.originalFromAddress ||
            caseResponse.case.originalToAddress) && (
          <Row
            style={{
              marginTop: 20,
            }}
          >
            <AddressRow>
              <AddressContent>
                <KeyValueList
                  colonKey
                  rows={[
                    {
                      key: 'Original från-adress',
                      value: formatAddress({
                        name: caseResponse.case.originalFromName ?? '-',
                        address: caseResponse.case.originalFromAddress ?? '-',
                        zip: caseResponse.case.originalFromZip ?? '-',
                        city: caseResponse.case.originalFromCity ?? '-',
                        county: caseResponse.case.originalFromCounty,
                      }),
                    },
                    {
                      key: 'Original till-adress',
                      value: formatAddress({
                        name: caseResponse.case.originalToName ?? '-',
                        address: caseResponse.case.originalToAddress ?? '-',
                        zip: caseResponse.case.originalToZip ?? '-',
                        city: caseResponse.case.originalToCity ?? '-',
                        county: caseResponse.case.originalToCounty,
                      }),
                    },
                  ]}
                />
              </AddressContent>
              <EditButton
                onClick={handleEditAddressClick}
                title="Redigera originaladresser"
                type="button"
              >
                <FontAwesomeIcon icon={faEdit} />
              </EditButton>
            </AddressRow>
          </Row>
        )
      }
      {assignmentWarnings.getCaseWarnings(caseResponse.case).map((warning) => (
        <Warning key={warning.warning}>
          {warning.icon ?? <FontAwesomeIcon icon={faWarning} />}{' '}
          {warning.warning}
        </Warning>
      ))}
    </Wrapper>
  );
};

export default CaseDataViewer;
